import { Injectable, ElementRef, NgZone, ApplicationRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject ,  Subscription } from 'rxjs';
import { IAutocompleteTrigger } from '@common/interfaces/i-autocomplete-trigger';
import { LogonService } from '@common/services/logon-service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { BasicContentService } from '@common/services/basic-content-service';
import { AttachmentService } from '@common/services/attachment-service';
import { UserLocationService } from '@common/services/user-location-service';
import { RealtimeUpdatesSignalR } from '@common/services/realtime-updates-signalr';
import { DataStore } from '@common/data-models/data-store/data-store';
import { ObjectTypes } from '@common/enums/object-types';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { AppTheme } from '@suite/app.theme';
import { Record } from '@common/data-models/records/record';
import { ExportFormatRecord } from "@common/data-models/records/export-format-record";
import { UserRecord } from "@common/data-models/records/user-record";
import { SearchResults } from "@common/data-models/search-results/search-results";
import { UsersResults } from '@common/data-models/search-results/users-result';
import { ServiceResponse } from "@common/data-models/service/service-response";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { Clipboard } from '@platform/components/clipboard/clipboard';
import { LayoutState } from "@common/services/layout-state";
import { ServiceRecord } from "@common/data-models/service/service-record";
import { TimeoutQueue } from '@common/services/timeout-queue';
import { NavigationService } from '@common/services/navigation-service';
import { LanguageSettings } from '@common/services/language-settings';
import { VisualStateService } from '@common/services/visual-state-service';

import { IAppService } from "@suite/i-app.service";
import { ClipboardTypes } from '@suite/common/enums/clipboard-types';
import { EnpsRecordControl } from '@common/controls/enps-record-control/enps-record-control';
import { MapService } from './common/services/map-service';
import { Dialog } from '@angular/cdk/dialog';
import { AttachmentErrorComponent } from './common/components/attachments-list/modal/error/attachment-error';

declare var serverConfig: { [id: string]: any };
declare var ssoSettings: Array<any>;

@Injectable({ providedIn: 'root' })
export class AppService implements IAppService {
    public readonly MAX_TITLE_LENGTH: number = 50;
    public PSTFieldNames = PSTFieldNames;

    public applicationName: string = "";
    public applicationEmbeddedName: string = "";

    public newTemplateSaveAs: { [id: string]: (database: string, fullPath: string, guid: string, groupId: string, refresh: boolean, referalId: string, oldRecord: Record, isNew?: boolean, mobile?: boolean) => Promise<ServiceResponse<Record>> } = {};

    // Clipboard
    public clipboard: Clipboard;

    // Sidebar
    public sideBarExpanded: boolean = true;

    public loginMessage: string = "";
    public loginShowAlert: boolean = false;

    //Filter

    private filterHidden: boolean = false;
    private filterHiddenSource = new Subject<boolean>();
    public filterHiddenObservable = this.filterHiddenSource.asObservable();

    public LoadingData: boolean = false;
    public LoadingDataSource = new Subject<boolean>();
    public LoadingDataObservable = this.LoadingDataSource.asObservable();

    //about menu

    // Topbar user menu
    public isUserMenuOpen: boolean = false;
    public previousUrl = '';

    private helloResultsSubscription: Subscription;

    // Record property
    private recordChangedSource = new Subject<Record>();
    public recordChangedObservable = this.recordChangedSource.asObservable();

    //ENPS Desktop

    //Mobile Top Bar

    public redirectedPath = null;

    private _groupByUsers: Array<{ id: string, title: string }> = [];
    public get groupByUsers(): Array<{ id: string, title: string }> {
        if ((this._groupByUsers.length == 0) && this.languageSettings) {
            this._groupByUsers = [
                { id: PSTFieldNames.UserInfoStatus, title: this.languageSettings.PSTWebUserGroupSortByStatusLabel },
                { id: PSTFieldNames.UserInfoProgram, title: this.languageSettings.PSTWebUserGroupSortByProgramLabel },
                { id: PSTFieldNames.UserInfoRole, title: this.languageSettings.PSTWebUserGroupSortByRoleLabel }
            ];
        }
        return this._groupByUsers;
    }

    private _groupByUsersKeys: Array<string> = [];
    public get groupByUsersKeys(): Array<string> {
        if ((this._groupByUsersKeys.length == 0) && (this.groupByUsers.length > 0)) {
            this._groupByUsersKeys = this.groupByUsers.map(item => item.id);
        }
        return this._groupByUsersKeys;
    }

    //SSO providers
    public ssoProviders: Array<any>;
    public ssoConfigSettings: any;
    public loginWithSSOprovidersOnly: boolean = true;

    public userResources: Array<UserRecord> = [];

    public appSupportMap: { [id: string]: { supportsSearchMode: boolean, redirectTo: string, supportsDuplicate: boolean, supportsClone: boolean, supportsSaveTemplate: boolean, supportsCopyToClipboard: boolean } } = {
        "playbook": { supportsSearchMode: true, redirectTo: "playbook", supportsDuplicate: true, supportsClone: true, supportsSaveTemplate: true, supportsCopyToClipboard: true },
        "cast": { supportsSearchMode: false, redirectTo: "playbook", supportsDuplicate: false, supportsClone: false, supportsSaveTemplate: false, supportsCopyToClipboard: false }
    }

    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public logonService: LogonService,
        public globalTablesService: GlobalTablesService,
        public basicContentService: BasicContentService,
        public attachmentService: AttachmentService,
        public userLocationService: UserLocationService,
        private realtimeUpdatesSignalR: RealtimeUpdatesSignalR,
        public theme: AppTheme,
        public router: Router,
        public activatedRoute: ActivatedRoute,
        public viewStateService: LayoutState,
        public ngZone: NgZone,
        public applicationRef: ApplicationRef,
        public timeoutQueue: TimeoutQueue,
        public navigationService: NavigationService,
        public languageSettings: LanguageSettings,
        public visualStateService: VisualStateService,
        public dialogService: Dialog,
        public mapService: MapService
    ) {
        DataStore.appService = this;
        Record.timeoutQueue = this.timeoutQueue;
        SearchResults.appService = this;
        UsersResults.appService = this;
        EnpsRecordControl.appService = this;
        EnpsRecordControl.globalTablesService = this.globalTablesService;
        EnpsRecordControl.basicContentService = this.basicContentService;
        DataStore.addRealtimeUpdatesProvider(this.realtimeUpdatesSignalR);

        if (!window["ReconnectRTU"]) {
            window["ReconnectRTU"] = () => {
                console.log("ReconnectRTU");
            };
        }

        if (this.nomWebServicesJson.HelloResults) {
            this.helloResultsLoaded({ loaded: true, login: false });
        } else {
            if (this.helloResultsSubscription) {
                try { this.helloResultsSubscription.unsubscribe(); } catch (e) { }
            }
            this.helloResultsSubscription = this.nomWebServicesJson.HelloResultsLoadedObservable.subscribe(this.helloResultsLoaded.bind(this));
        }

        // Start Ping thread
        setInterval(() => {
            if (!this.nomWebServicesJson.noSession) {
                this.nomWebServicesJson.ping();
            }
        }, 60000);

    }

    public forceReload() {
        if (this.visualStateService.hasIOSInterOp()) {
            window["webkit"].messageHandlers.interOp.postMessage({ "name": "forceReload", "url": encodeURI(window.location.href) });
        }
    }

    public setLoginError() {
        if (this.visualStateService.hasIOSInterOp()) {
            window["webkit"].messageHandlers.interOp.postMessage({ "name": "loginError", "result": "true" });
        }
        if (window["ANDROID_WRAPPER"]) {
            window["ANDROID_WRAPPER"].logout();
        }
    }

    public getObjectTypeName(objectType: number) {
        switch (objectType) {
            case ObjectTypes.STORYBOARD:
                return "STORYBOARD";
            case ObjectTypes.RUNDOWN:
                return "RUNDOWN";
            case ObjectTypes.SCRIPT:
                return "STORY";
            default:
                return "undefined";
        }
    }

    // Util
    public stopEventPropagation(event: any) {
        event.stopPropagation();
    }

    public isClickedOutsideOfComponent(event: any, elementRef: any) {
        let clickedComponent = event.target;
        let inside = false;
        do {
            if (clickedComponent === elementRef.nativeElement) {
                inside = true; //clicked inside the component
            }
            clickedComponent = clickedComponent.parentNode;
        } while (clickedComponent);
        if (inside) {
            return true;
        } else {
            return false;
        }
    }

    // Sidebar
    public isSideBarExpanded() {
        if (this.visualStateService.isDesktop) return false;
        return this.sideBarExpanded;
    }


    //Navigation

    //Attachments
    public get attachmentsAllowed(): boolean {
        return (this.visualStateService.ConfigSettings["ExternalDocuments"] == "1");
    }

    //Field groups order
    public get fieldGroupsOrder(): string {
        if (this.visualStateService.ConfigSettings["FieldGroupsOrder"]) {
            return this.visualStateService.ConfigSettings["FieldGroupsOrder"];
        }
        return "";
    }

    //Cast restricted publication channel types
    public get restrictedPublicationChannelTypes(): Array<String> {
        if (this.visualStateService.ConfigSettings["PSTRestrictedPublicationChannelTypes"]) {
            let res = (<string>this.visualStateService.ConfigSettings["PSTRestrictedPublicationChannelTypes"]).split('|');
            return res;
        }
        return [];
    }

    public get allowedContentTypes(): Array<string> {
        return this.visualStateService.ConfigSettings['AllowedExternalDocs']?.split(',')?.map( e => e.trim().toLowerCase()) || [];
    }

    public get allowedContentTypesLabel(): string {
        return this.visualStateService.ConfigSettings['AllowedExternalDocs']?.split(',') ?.length > 0 ? this.visualStateService.ConfigSettings['AllowedExternalDocs']?.split(',') : 'NONE';
    }

    public isFileAllowedForUpload(filename: string) {
        const chunks = filename.split('.');
        const fileExtension = chunks[chunks.length - 1]?.toLowerCase();
        return this.allowedContentTypes.includes(fileExtension);
    }

    public async readUrl(files: Array<any>) {
        let base64Files = [];
        let res = [];
        for (let file in files) {
            let reader = new FileReader();
            let ofile = file;
            reader.onloadend = (event) => {
                files[ofile].finished = true;
                let base64File = event.target.result.toString().split('base64,')[1];
                res.push({ Type: ObjectTypes.EXTERNAL, FileBytes: base64File, Title: files[ofile].name, Filename: files[ofile].name });
            }
            reader.readAsDataURL(files[ofile].file);
        }
        await this.checkState(files);
        return res;
    }

    public async checkState(files) {
        let unfinished: boolean = false;
        while (!unfinished) {
            for (let file of files) {
                if (!file.finished) unfinished = true;
            }
            if (unfinished) await (new Promise(resolve => setTimeout(resolve, 100)));
        }
    }

    // Autocomplete scroll
    public autoCompleteInstances: Array<IAutocompleteTrigger> = [];
    public fixAutocompleteScrollPositions(currentOpen?: IAutocompleteTrigger) {
        for (let inst of this.autoCompleteInstances) {
            if (currentOpen && (currentOpen != inst) && inst.getAutocompleteTrigger()) {
                inst.getAutocompleteTrigger().closePanel();
            }
            if (inst && inst.getAutocompleteTrigger() && inst.getAutocompleteTrigger().panelOpen && (<any>inst.getAutocompleteTrigger())._overlayRef) {
                (<any>inst.getAutocompleteTrigger())._overlayRef.updatePosition();
            }
        }
    }

    public async loadSessionData(): Promise<boolean> {
        try {
            let userId = localStorage.getItem("SessionUserId");
            let nomTokenId = localStorage.getItem("SessionNomTokenId");
            let helloResults = localStorage.getItem("GlobalsHelloResults");
            let hasSSOProviders: boolean = !!JSON.parse(localStorage.getItem("HasSSOProviders")) || ssoSettings?.length > 0;
            this.ssoProviders = ssoSettings || [];
            if (userId && nomTokenId && helloResults) {
                this.nomWebServicesJson.UserId = userId;
                this.nomWebServicesJson.NomTokenId = nomTokenId;
                this.nomWebServicesJson.HelloResults = JSON.parse(helloResults);
                this.nomWebServicesJson.HasSSOProviders = hasSSOProviders;

                this.visualStateService.getExpanderSettings();
                if (this.logonService.logonPassed) {
                    await this.helloResultsLoaded({ loaded: true, login: true });
                    return true;
                }
                let pingRes = await this.logonService.ping();
                if (pingRes && pingRes.status) {
                    await this.helloResultsLoaded({ loaded: true, login: false });
                    return true;
                }
                this.nomWebServicesJson.clearSessionData();
                return false;
            } else {
                this.nomWebServicesJson.clearSessionData();
                return false;
            }
        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            return true;
        }
    }

    public async helloResultsLoaded(status: any) {
        if (status.loaded) {
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.Topic] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.Assignment] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.TopicView] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.AssignmentView] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.SearchView] = 0;
            if (this.nomWebServicesJson.HelloResults.PersonalClipboardTemplates && this.nomWebServicesJson.HelloResults.PersonalClipboardTemplates.length) {
                let tmpEfTemplates: { [id: string]: ExportFormatRecord } = {};
                for (let rec of this.nomWebServicesJson.HelloResults.PersonalClipboardTemplates) {
                    let serviceRecord: ServiceRecord = ServiceRecord.instance(rec);
                    let tmpRec = this.nomWebServicesJson.Convertor.fromServiceExportFormatRecord(serviceRecord);
                    tmpEfTemplates[tmpRec.exportFormatSourceObjectType] = tmpRec;
                    this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[tmpRec.exportFormatSourceObjectType]++;
                }
                this.nomWebServicesJson.HelloResults.PersonalExportFormatTemplates = tmpEfTemplates;
            }
            if (this.nomWebServicesJson.HelloResults.TeamClipboardTemplates && this.nomWebServicesJson.HelloResults.TeamClipboardTemplates.length) {
                let tmpEfTemplates: { [id: string]: ExportFormatRecord } = {};
                for (let rec of this.nomWebServicesJson.HelloResults.TeamClipboardTemplates) {
                    let serviceRecord: ServiceRecord = ServiceRecord.instance(rec);
                    let tmpRec = this.nomWebServicesJson.Convertor.fromServiceExportFormatRecord(serviceRecord);
                    tmpEfTemplates[tmpRec.exportFormatSourceObjectType] = tmpRec;
                    this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[tmpRec.exportFormatSourceObjectType]++;
                }
                this.nomWebServicesJson.HelloResults.TeamExportFormatTemplates = tmpEfTemplates;
            }
            if (this.nomWebServicesJson.HelloResults.ClipboardTemplates && this.nomWebServicesJson.HelloResults.ClipboardTemplates.length) {
                let tmpEfTemplates: { [id: string]: ExportFormatRecord } = {};
                for (let rec of this.nomWebServicesJson.HelloResults.ClipboardTemplates) {
                    let serviceRecord: ServiceRecord = ServiceRecord.instance(rec);
                    let tmpRec = this.nomWebServicesJson.Convertor.fromServiceExportFormatRecord(serviceRecord);
                    tmpEfTemplates[tmpRec.exportFormatSourceObjectType] = tmpRec;
                    this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[tmpRec.exportFormatSourceObjectType]++;
                }
                this.nomWebServicesJson.HelloResults.EnterpriseExportFormatTemplates = tmpEfTemplates;
            }
        }
    }

    private solrPollInterval: number;
    public getSolrPollInvervalInSeconds() {
        if (this.solrPollInterval) {
            return this.solrPollInterval;
        }
        else {
            this.solrPollInterval = !parseInt(serverConfig["SolrPollIntervalSeconds"]) ? 10000 : parseInt(serverConfig["SolrPollIntervalSeconds"]) * 1000;
            return this.solrPollInterval;
        }
    }

    public isAssignmentLayoutState(): boolean {
        return (this.viewStateService.ViewStateRecord && this.viewStateService.ViewStateRecord.viewStateType == ViewStateTypes.ASSIGNMENT);
    }

    public isSearchLayoutState(): boolean {
        return (this.viewStateService.ViewStateRecord && this.viewStateService.ViewStateRecord.viewStateType == ViewStateTypes.SEARCH);
    }

    // Record
    public recordChanged(record: Record) {
        this.recordChangedSource.next(record);
    }

    public delayTimelineInit: boolean = false;

    public async scrollCurrentListViewToTop() {
        if (this.navigationService.currentListView && this.navigationService.currentListView.container && this.navigationService.currentListView.container.nativeElement) {
            this.navigationService.currentListView.container.nativeElement.scrollTop = 0;
        }
    }

    public isPreviousRouteList(): boolean {
        if (!this.previousUrl) this.previousUrl = localStorage.getItem('prevUrl');
        if (this.previousUrl.indexOf('filter') > -1 ||
            this.previousUrl.indexOf('groupby') > -1 ||
            this.previousUrl.indexOf('sortby') > -1 ||
            this.previousUrl.indexOf('add_topic') > -1 ||
            this.previousUrl.indexOf('view_topic') > -1 ||
            this.previousUrl.indexOf('add_assignment') > -1 ||
            this.previousUrl.indexOf('edit_assignment') > -1
        ) {
            return false;
        }
        return true;

    }

    public getUserID(rec: Record): string {
        let storyOwner = rec.storyOwner;
        let owner = rec.owner;
        if (storyOwner) {
            let owners = storyOwner;
            if (owners.length > 0) {
                return owners[0];
            }
        } else if (owner && rec instanceof UserRecord) {
            return owner;
        }
        return '';
    }

    public getUserName(rec: Record): string {
        let storyOwner = rec.storyOwner;
        let objectType = rec.type;

        if (storyOwner) {
            let owners = storyOwner;
            let userID = '';
            if (owners.length >= 1) {
                userID = owners[0];
                let name = this.globalTablesService.Staff[userID] ? this.globalTablesService.Staff[userID].GivenName + ' ' + this.globalTablesService.Staff[userID].Surname : 'Unknown';
                return name;
            }
        }
        if (rec instanceof UserRecord) {
            let userID = rec.owner;
            if (userID) {
                return this.globalTablesService.Staff[userID] ? this.globalTablesService.Staff[userID].GivenName + ' ' + this.globalTablesService.Staff[userID].Surname : 'Unknown';
            }
        }

        if (objectType == ObjectTypes.TOPIC) {
            return this.languageSettings.PSTWebStaffIDPlaceholder;
        }
        if (objectType == ObjectTypes.ASSIGNMENT) {
            return this.languageSettings.PSTWebAssignmentOwnerLabel;
        }
        return "";
    }

    //Mobile controls
    public onDialogHide() {
        this.timeoutQueue.setTimeout(() => {
            this.applicationRef.tick();
        }, 10, null, true);
    }

    public async shouldProceedAttachment(name: string, size: number): Promise<boolean> {
        if (!this.isFileAllowedForUpload(name) || size > this.attachmentService.maxExternalDocumentSize) {
            await this.openAttachmentErrAlert(name);
            return false;
        }
        return true;
    }

    public async openAttachmentErrAlert(name?: string) {
        await this.dialogService.open(AttachmentErrorComponent, {
            data: {
                title: `${this.languageSettings.PSTWebErrorUploadingFileLabel}${((name?.length > 0) ? `: ${name}` : '')}`,
                messages: [
                    `${this.languageSettings.PSTWebAllowedFileTypesText} ${this.allowedContentTypesLabel}`,
                    `${this.languageSettings.PSTWebAllowedFileSizeText} ${this.attachmentService.maxExternalDocumentSize / 1000} kb`
                ]

            }
        }).closed.toPromise();
    }

}
